import { createStore, Store } from "vuex";
import { User } from "@microsoft/microsoft-graph-types-beta";

import CaseAssignmentModule from "@/store/modules/CaseAssignment";
import CaseActionsModule from "./modules/CaseActions";
import NotificationsModule from "./modules/Notifications";
import TransactionsModule from "./modules/CaseTransactions";
import { CaseStats } from "@/types/stats";
import {
  ICaseInfo,
  ICaseResponse,
  ICaseReasonCode,
  CaseType,
  ICaseCountry,
  ICaseStore
} from "@/types/casesFrontendTypes";
import CaseFilterModule from "./modules/CaseFilter";
import {
  validateProfileData,
  translateCaseDeta,
  createServerEndpoint,
  tagRiskPattern,
  extractRiskPatternType,
  ajaxErrorHandler,
  extractAJAXErrorValues,
  getDateRange
} from "@/utils/functions";
import staticCaseFilters from "@/utils/data/case-filters";
import {
  DynamicCaseFilterDropdownData,
  CaseFilterDropdownData,
  UserProfileData,
  PageLoadError,
  RiskPatternItem
} from "@/rca-types";
import { RC, RiskPattern } from "@/utils/constants";
import { IUserProfile } from "@/types/users";
import ReportingModule from "./modules/Reporting";
import ControlsModule from "./modules/Controls";
import AMLModule from "./modules/AML";
import { IStatisticsResponse } from "../../../rca-app-server/src/cases/casesTypes";
import axios from "axios";
import { fetchCsrfToken } from "@/services/csrfService";
import {
  buildUserScopedCacheKey,
  cloneCacheValue,
  ONE_HOUR_MS,
  pruneExpiredCacheEntries,
  setCacheEntryWithLimit
} from "@/store/cache-utils";

type ControlDashboardEntry = {
  data: IStatisticsResponse | null;
  loading: boolean;
  error: string | null;
  url: string;
};

type ControlDashboardState = Record<string, ControlDashboardEntry>;

type ControlDashboardCacheEntry = {
  fetchedAt: number;
  data: ControlDashboardState;
};

const CONTROL_DASHBOARD_CACHE_TTL_MS = ONE_HOUR_MS;
const CONTROL_DASHBOARD_CACHE_MAX_ENTRIES = 50;
const controlDashboardCache = new Map<string, ControlDashboardCacheEntry>();
const controlDashboardRequests = new Map<string, Promise<void>>();
const controlFilterOptionRequests = new Map<string, symbol>();
const dashboardControls = [
  { code: RC.TD_CODE, types: [RC.TD_CODE], url: 'tender-deviations' },
  { code: RC.AC_CODE, types: [RC.AC_CODE, RC.NAC_CODE], url: 'article-non-article' },
  { code: RC.RR_CODE, types: [RC.RR_CODE], url: 'receiptless-returns' },
  { code: RC.RPA_CODE, types: [RC.RPA_CODE], url: 'return-price-adjustments' },
  { code: RC.SG80_CODE, types: [RC.SG80_CODE], url: 'sales-group-80' }
];

function buildControlDashboardCacheKey(params: any, userId: string): string {
  return buildUserScopedCacheKey(params, userId, "params");
}

// Define the root state interface
export interface RootState {
  dataset: ICaseResponse | null;
  riskPatternStats: ICaseResponse | null;
  controlDashboard: ControlDashboardState;
  cachedReasonCodes: ICaseReasonCode[] | null;
  currentCase: ICaseInfo | null;
  csrfToken: string | null;
  user: {
    isAuthenticated: boolean;
    profileData: UserProfileData | null;
  };
  pageLoadingError: PageLoadError | null;
  users: Record<string, any>;
  currentUser: User | null;
  currentUserRoles: any[] | null;
  tabsToShow: any[] | null;
  isCasesGridLoading: boolean;
  paging: {
    page: number;
    page_size: number;
    total: number;
  };
    tokenValid: boolean;
    tokenPollingInterval: number | null;
  }
const storeConfig = {
  state: (): RootState => ({
    dataset: null,
    riskPatternStats: null,
    controlDashboard: {},
    cachedReasonCodes: null,
    currentCase: null,
    csrfToken: null,
    user: {
      isAuthenticated: false,
      profileData: null
    },
    pageLoadingError: null,
    users: {},
    currentUser: null,
    currentUserRoles: null,
    tabsToShow: null,
    isCasesGridLoading: false,
    paging: {
      page: 1,
      page_size: 15,
      total: 0
    },
    tokenValid: false,
    tokenPollingInterval: null
  }),
  getters: {
    CURRENT_CASE_ID: (state: RootState): string => {
      if (state.currentCase) return state.currentCase.case_id;
      return "";
    },
    USER_PROFILE_READY: (state: RootState): boolean => {
      const { user } = state;
      return user.profileData != null;
    },
    DASHBOARD_READY: (state: RootState): boolean => {
      const { user, dataset, riskPatternStats } = state;
      return (
        dataset != null &&
        user.profileData != null &&
        riskPatternStats != null
      );
    },
    DASHBOARD_CONTROL_READY: (state: RootState): boolean => {
      const { user, controlDashboard } = state;
      if (!user.profileData) return false;
      if (!controlDashboard) return false;
      
      const controlValues = Object.values(controlDashboard);
      // Ready when we have controls and at least one has finished loading (success or error)
      if (controlValues.length === 0) return false;
      
      return controlValues.some(control => !control.loading);
    },
    CASES: (state: RootState): ICaseInfo[] => {
      const { dataset } = state;
      if (dataset == null) {
        return [];
      } else {
        return translateCaseDeta(dataset.case || dataset);
      }
    },
    PAGING_OPTIONS: (state: RootState): any => {
      const { paging } = state;
      return paging;
    },
    STATS: (state: RootState): CaseStats => {
      const { riskPatternStats } = state;
      if (riskPatternStats == null) {
        return {
          status: {
            new: -1,
            investigating: -1,
            closed: -1,
            assigned: -1,
            overdue: -1
          },
          risk: {
            low: -1,
            medium: -1,
            high: -1
          }
        };
      } else {
        const { caseStat: statsData } = riskPatternStats;
        return {
          status: {
            new: statsData.status.new,
            investigating: statsData.status.investigating,
            closed: statsData.status.resolved || -1, // Map resolved to closed
            assigned: statsData.status.assigned,
            overdue: statsData.status.overdue
          },
          risk: {
            low: statsData.risk.low,
            medium: statsData.risk.medium,
            high: statsData.risk.high
          }
        };
      }
    },
    COUNTRY_DATA: (state: RootState): ICaseCountry[] => {
      const { riskPatternStats } = state;
      if (riskPatternStats == null) {
        return [];
      } else {
        const { caseOption: options } = riskPatternStats;
        const { country } = options;
        return country as ICaseCountry[];
      }
    },
    STORE_DATA: (state: RootState): ICaseStore[] => {
      const { riskPatternStats } = state;
      if (riskPatternStats == null) {
        return [];
      } else {
        const { caseOption: options } = riskPatternStats;
        const { store } = options;
        return store;
      }
    },
    RISK_PATTERN_DATA: (): RiskPatternItem[] => {
      const patternData = [];
      let patternKey: CaseType;
      for (patternKey in RiskPattern) {
        patternData.push({
          id: patternKey,
          description: RiskPattern[patternKey]
        });
      }
      return patternData;
    },
    FILTERS: (state: RootState): CaseFilterDropdownData => {
      const { riskPatternStats } = state;
      let filters: CaseFilterDropdownData;
      if (riskPatternStats == null) {
        filters = {
          assignment: [],
          country: [],
          date: [],
          progressStatus: [],
          reasonCode: [],
          riskPattern: [],
          storeId: [],
          riskLevel: []
        };
      } else {
        const { caseOption: options } = riskPatternStats;
        const { country, store, assignment } = options;

        const isNotMyProfile = (item: any) =>
          item != null && item.id !== state.currentUser?.id;

        const otherProfiles = assignment.filter(isNotMyProfile);

        const dynamicCaseFilters: DynamicCaseFilterDropdownData = {
          country: country.map((item: any) => ({
            text: item.country_name,
            value: item.country_code
          })),

          storeId: store
            .filter((item: any) => item != null)
            .map((item: any) => ({
              text: item.store_name,
              value: item.store_code
            })),

          assignment: [
            { text: "Anybody", value: "" },
            { text: "Unassigned", value: "Unassigned" },
            { text: "Assigned to me", value: state.currentUser?.id || "" },
            ...otherProfiles.map((item: any) => ({
              text: item.displayName || 'Unknown User',
              value: item.id || ''
            }))
          ],

          reasonCode: []
        };

        filters = {
          ...staticCaseFilters,
          ...dynamicCaseFilters
        };
      }
      return filters;
    },
    csrfToken: (state: RootState) => state.csrfToken,
  },
  mutations: {
    SAVE_DATASET(state: RootState, payload: ICaseResponse) {
      state.dataset = payload;
    },
    SAVE_PAGING_OPTIONS(state: RootState, payload: any) {
      state.paging = {
        page: payload.page,
        page_size: payload.page_size,
        total: payload.total
      };
    },
    SAVE_RP_STATS(state: RootState, payload: ICaseResponse) {
      state.riskPatternStats = payload;
    },
    SAVE_CONTROL_DASHBOARD(state: RootState, payload: any) {
      state.controlDashboard = payload;
    },
    INCREMENT_STATUS_COUNT(state: RootState, field: string) {
      try{
        const controlDashboard = state.controlDashboard;
        if (controlDashboard && typeof controlDashboard === 'object') {
          Object.keys(controlDashboard).forEach(key => {
            const control = controlDashboard[key];
            if (control.data && control.data.status) {
              const status = control.data.status as any;
              status[field] = (status[field] || 0) + 1;
            }
          });
        }
      } catch(e){
        console.error("Error while updating dashboard statistics in UI. Backend update is async and doesn't affect. Refresh page to see latest counts");
      }
    },
    DECREMENT_STATUS_COUNT(state: RootState, field: string) {
      try{
        const controlDashboard = state.controlDashboard;
        if (controlDashboard && typeof controlDashboard === 'object') {
          Object.keys(controlDashboard).forEach(key => {
            const control = controlDashboard[key];
            if (control.data && control.data.status) {
              const status = control.data.status as any;
              status[field] = Math.max(0, (status[field] || 0) - 1);
            }
          });
        }
      } catch(e){
        console.error("Error while updating dashboard statistics in UI. Backend update is async and doesn't affect. Refresh page to see latest counts");
      }
    },
    RESET_CONTROL_DASHBOARD(state: RootState) {
      state.controlDashboard = {};
    },
    RESET_CONTROL_FILTERS(state: RootState) {
      // Note: This should be handled by the Controls module
      // For now, this is a placeholder
    },
    UPDATE_CURRENT_CASE(state: RootState, payload: ICaseInfo) {
      state.currentCase = payload;
    },
    UPDATE_AUTH_STATE(state: RootState, payload: UserProfileData) {
      const isValid = validateProfileData(payload);
      state.user.isAuthenticated = isValid;
      if (isValid) {
        state.user.profileData = payload;
      }
    },
    SAVE_PROFILE_DATA(state: RootState, payload: User) {
      state.currentUser = payload;
    },
    SAVE_USER_ROLES(state: RootState, payload: any) {
      state.currentUserRoles = payload;
    },
    CACHE_REASON_CODES(state: RootState, payload: ICaseReasonCode[]) {
      state.cachedReasonCodes = payload;
    },
    REGISTER_PAGE_LOAD_ERROR(state: RootState, error: PageLoadError) {
      state.pageLoadingError = error;
    },
    CLEAR_PAGE_LOAD_ERROR(state: RootState) {
      state.pageLoadingError = null;
    },
    UPDATE_TABS_TO_SHOW(state: RootState, payload: any) {
      state.tabsToShow = payload;
    },
    UPDATE_CASES_FETCH(state: RootState, payload: boolean) {
      state.isCasesGridLoading = payload;
    },
    UPDATE_TOKEN_VALIDITY(state: RootState, isValid: boolean) {
      state.tokenValid = isValid;
    },
    SET_TOKEN_POLLING_INTERVAL(state: RootState, intervalId: number | null) {
      state.tokenPollingInterval = intervalId;
    },
    SET_CSRF_TOKEN(state: RootState, token: string) {
      state.csrfToken = token;
    },
    SET_CONTROL_LOADING(state: RootState, payload: { controlType: string, url: string }) {
      if (!state.controlDashboard) {
        state.controlDashboard = {};
      }
      state.controlDashboard[payload.controlType] = {
        data: null,
        loading: true,
        error: null,
        url: payload.url
      };
    },
    SET_CONTROL_DATA(state: RootState, payload: any) {
      if (state.controlDashboard) {
        state.controlDashboard[payload.controlType] = {
          data: payload.data,
          loading: false,
          error: payload.error,
          url: payload.url
        }
      }
    },
    SET_CONTROL_FILTER_OPTIONS(state: RootState, payload: any) {
      const control = state.controlDashboard[payload.controlType];
      if (control?.data) {
        control.data = {
          ...control.data,
          status: payload.status,
          risk: payload.risk,
          totalCases: payload.totalCases,
          caseOption: payload.caseOption
        };
      }
    }
  },
  actions: {
    async FETCH_PROFILE_DATA(context: any) {
      const profileEndpoint = createServerEndpoint("/users/me");
      try {
        const {
          data: { profile, adRoles }
        } = await axios.get<IUserProfile>(profileEndpoint);
        const { givenName: firstname, surname: lastname } = profile;
        context.commit("UPDATE_AUTH_STATE", { 
          firstname: firstname || '', 
          lastname: lastname || '' 
        });
        context.commit("SAVE_PROFILE_DATA", profile);
        context.commit("SAVE_USER_ROLES", adRoles);
      } catch (error: any) {
        if (error?.response?.status === 401) {
          if (context.state.tokenPollingInterval) {
            clearInterval(context.state.tokenPollingInterval);
            context.commit("SET_TOKEN_POLLING_INTERVAL", null);
          }
          window.location.href = "/logout";
          return;
        }
        const pageError = {
          code: error?.response?.status as number,
          endpoint: error?.response?.config?.url,
          summary: "Fetching Profile Data failed"
        } as PageLoadError;
        context.commit("REGISTER_PAGE_LOAD_ERROR", pageError);
      }
    },
    async FETCH_CASES(context: any, params: any = null) {
      if (context.state.isCasesGridLoading) {
        console.log("Skipping Risk Patterns API call - already loading");
        return;
      }
      
      const casesEndPoint = createServerEndpoint("/api/cases");
      if (!params || !params.page) {
        params = {
          page: 1,
          page_size: 15,
          sort_column: "risk_score",
          sort_direction: "asc"
        };
      }
      try {
        context.commit("UPDATE_CASES_FETCH", true);
        const { data } = await axios.get<ICaseResponse>(casesEndPoint, {
          params: params
        });
        context.commit("SAVE_DATASET", data);
        context.commit("SAVE_PAGING_OPTIONS", data);
      } catch (err: any) {
        const extracted = extractAJAXErrorValues(err);
        const formattedError = ajaxErrorHandler(extracted);
        console.error(formattedError);
        context.commit("REGISTER_PAGE_LOAD_ERROR", formattedError);
      } finally {
        context.commit("UPDATE_CASES_FETCH", false);
      }
    },
    async FETCH_CASES_STATS(context: any, params: any = null) {
      const casesEndPoint = createServerEndpoint("/api/cases/summary");
      try {
        const { data } = await axios.get<ICaseResponse>(casesEndPoint, {
          params: params
        });
        context.commit("SAVE_RP_STATS", data);
      } catch (err: any) {
        const extracted = extractAJAXErrorValues(err);
        const formattedError = ajaxErrorHandler(extracted);
        console.error(formattedError);
        context.commit("REGISTER_PAGE_LOAD_ERROR", formattedError);
      }
    },
    async FETCH_CONTROL_DASHBOARD(context: any, params: any = {}) {
      context.commit("CLEAR_PAGE_LOAD_ERROR");

      const userId = context.state.currentUser?.id || "anonymous";
      const cacheKey = buildControlDashboardCacheKey(params, userId);
      pruneExpiredCacheEntries(
        controlDashboardCache,
        CONTROL_DASHBOARD_CACHE_TTL_MS
      );
      const cached = controlDashboardCache.get(cacheKey);
      const isFresh =
        cached && Date.now() - cached.fetchedAt < CONTROL_DASHBOARD_CACHE_TTL_MS;

      if (isFresh) {
        context.commit(
          "SAVE_CONTROL_DASHBOARD",
          cloneCacheValue(cached.data)
        );
        return;
      }

      const pendingRequest = controlDashboardRequests.get(cacheKey);
      if (pendingRequest) {
        await pendingRequest;
        return;
      }
      
      const casesEndPoint = createServerEndpoint("/api/risk-control/dashboard");
      const controls = dashboardControls;

      // Initialize all controls as loading
      controls.forEach(control => {
        context.commit("SET_CONTROL_LOADING", {
          controlType: control.code,
          url: control.url
        });
      });

      const requestPromise = (async () => {
        const requests = controls.map(async (control) => {
          try {
            const response = await axios.get<IStatisticsResponse[]>(casesEndPoint, {
              params: {
                ...params,
                risk_control_types: control.types
              },
              timeout: 30000
            });

            const apiData = response.data[0] || {};
            context.commit("SET_CONTROL_DATA", {
              controlType: control.code,
              // Use URL from backend (empty if user doesn't have access)
              url: apiData.url || control.url,
              data: apiData,
              error: null
            });
          } catch (error: any) {
            context.commit("SET_CONTROL_DATA", {
              controlType: control.code,
              url: control.url,
              data: null,
              error: error.message || "Failed to load statistics"
            });
          }
        });

        await Promise.all(requests);

        setCacheEntryWithLimit(
          controlDashboardCache,
          cacheKey,
          {
          fetchedAt: Date.now(),
          data: cloneCacheValue(context.state.controlDashboard)
          },
          CONTROL_DASHBOARD_CACHE_MAX_ENTRIES
        );
      })()
        .finally(() => {
          controlDashboardRequests.delete(cacheKey);
        });

      controlDashboardRequests.set(cacheKey, requestPromise);
      await requestPromise;
    },
    async FETCH_CONTROL_FILTER_OPTIONS(context: any, payload: {
      controlUrl: string;
      date_range: string;
      countries?: string[];
    }) {
      const control = dashboardControls.find(item => item.url === payload.controlUrl);
      if (!control) return;

      const requestId = Symbol();
      controlFilterOptionRequests.set(control.code, requestId);
      try {
        const response = await axios.get<IStatisticsResponse[]>(
          createServerEndpoint("/api/risk-control/dashboard"),
          {
            params: {
              ...getDateRange(payload.date_range),
              ...(payload.countries && { countries: payload.countries }),
              risk_control_types: control.types
            },
            timeout: 30000
          }
        );
        if (controlFilterOptionRequests.get(control.code) !== requestId) return;

        const summary = response.data[0];
        context.commit("SET_CONTROL_FILTER_OPTIONS", {
          controlType: control.code,
          status: summary?.status || {},
          risk: summary?.risk || {},
          totalCases: summary?.totalCases ?? 0,
          caseOption: summary?.caseOption || {}
        });
      } catch (error) {
        if (controlFilterOptionRequests.get(control.code) === requestId) {
          console.error("Failed to refresh Risk Controls filter options", error);
        }
      } finally {
        if (controlFilterOptionRequests.get(control.code) === requestId) {
          controlFilterOptionRequests.delete(control.code);
        }
      }
    },
    async FETCH_SINGLE_CASE(context: any, id: string) {
      const { commit } = context;
      const endpointRaw = createServerEndpoint("/api/cases/{case_id}/detail");
      const endpoint = endpointRaw.replace("{case_id}", id);

      try {
        const { data } = await axios.get<ICaseInfo>(endpoint);
        const transformedData = tagRiskPattern(
          data,
          extractRiskPatternType(data.case_id)
        );
        commit("UPDATE_CURRENT_CASE", transformedData);
      } catch (error: any) {
        const pageError = {
          code: error.response.status as number,
          endpoint: error.response.config.url,
          summary: "Fetching single-case failed"
        } as PageLoadError;
        context.commit("REGISTER_PAGE_LOAD_ERROR", pageError);
      }
    },
    async TRIGGER_CASE_PREVIEW(context: any, item: any) {
      context.commit("UPDATE_CURRENT_CASE", item);
    },
    async RESET_CONTROL_DASHBOARD(context: any, item: any) {
      context.commit("RESET_CONTROL_DASHBOARD");
    },
    async RESET_CONTROL_FILTERS(context: any) {
      context.commit("RESET_CONTROL_FILTERS");
    },
    async FETCH_REASON_CODES(context: any, pattern: CaseType) {
      const endpoint = createServerEndpoint(
        `/api/data/reason-codes/${pattern}`
      );
      try {
        const { data } = await axios.get<ICaseReasonCode[]>(endpoint);
        context.commit("CACHE_REASON_CODES", data);
      } catch (err) {
        console.error(err);
      }
    },
    async TABS_TO_SHOW(context: any, item: any) {
      context.commit("UPDATE_TABS_TO_SHOW", item);
    },
    async CASES_GRID_LOADING(context: any, item: any) {
      context.commit("UPDATE_CASES_FETCH", item);
    },
    async INCREMENT_STATUS_COUNT(context: any, field: string) {
      context.commit("INCREMENT_STATUS_COUNT", field);
    },
    async DECREMENT_STATUS_COUNT(context: any, field: string) {
      context.commit("DECREMENT_STATUS_COUNT", field);
    },
    async CHECK_TOKEN_VALIDITY(context: any) {
      // Don't check token if we're already on the signin page
      if (window.location.pathname === '/signin') {
        if (context.state.tokenPollingInterval) {
          clearInterval(context.state.tokenPollingInterval);
          context.commit("SET_TOKEN_POLLING_INTERVAL", null);
        }
        return;
      }

      try {
        const endpoint = createServerEndpoint("/users/token-info");
        const { data } = await axios.get<{ valid: boolean, minutesRemaning: number }>(endpoint);

        context.commit("UPDATE_TOKEN_VALIDITY", data.valid);

        if (!data.valid) {
          // Clear the polling interval before redirecting
          if (context.state.tokenPollingInterval) {
            clearInterval(context.state.tokenPollingInterval);
            context.commit("SET_TOKEN_POLLING_INTERVAL", null);
          }
          window.location.href = "/signin";
        }
      } catch (error) {
        context.commit("UPDATE_TOKEN_VALIDITY", false);
        // Clear interval on error too
        if (context.state.tokenPollingInterval) {
          clearInterval(context.state.tokenPollingInterval);
          context.commit("SET_TOKEN_POLLING_INTERVAL", null);
        }
      }
    },
    START_TOKEN_POLLING(context: any) {
      // Don't start polling if already running
      if (context.state.tokenPollingInterval) {
        return;
      }

      context.dispatch("CHECK_TOKEN_VALIDITY");

      const intervalId = setInterval(() => {
        context.dispatch("CHECK_TOKEN_VALIDITY");
      }, 3 * 60 * 1000) as unknown as number;
      
      context.commit("SET_TOKEN_POLLING_INTERVAL", intervalId);
    },
    async FETCH_CSRF_TOKEN(context: any) {
      try {
        const token = await fetchCsrfToken();
        if (!token) {
          throw new Error("CSRF token missing");
        }
        context.commit("SET_CSRF_TOKEN", token);
      } catch (error) {
        console.error('Failed to fetch CSRF token', error);
        context.commit("SET_CSRF_TOKEN", null);
        throw error;
      }
    }
  },
  modules: {
    CaseFilter: CaseFilterModule,
    CaseAssignment: CaseAssignmentModule,
    CaseActions: CaseActionsModule,
    Notifications: NotificationsModule,
    Transactions: TransactionsModule,
    Reporting: ReportingModule,
    Controls: ControlsModule,
    AML: AMLModule
  }
};

// Create and export the store
const store: Store<RootState> = createStore(storeConfig);
export default store;
